import { observer } from 'mobx-react-lite'
import {
  AppliedFiltersContainer,
  FilterButtonContainer,
} from '@/components/DataTable/FilterBarContainer'
import { DataTableRowSkeleton } from '@/components/DataTable'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import {
  UserList,
  UserListActionsButton,
  type UserListColumnId,
  userListColumns,
} from '@/features/iam/users/components/UserList'
import { NoResultsEmptyState } from '@/components/NoResultsEmptyState'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { useFeatures } from '@/contexts/root-store-context'
import { type UserManagementViewModel } from '@/features/user-management/UserManagement/UserManagementViewModel'
import { match } from '@/utils/pattern-match-util'
import { type SelectionState } from '@/infra/selection-state'
import { getUserDataTypeDisplayName } from '@/features/user-management/user-data-type-display'
import { UserManagementTableMenuContainer } from '@/features/user-management/components/UserManagementTableMenu/UserManagementTableMenuContainer'
import { UserRowActionsContainer } from '@/features/user-management/components/UserManagementTableRow/UserManagementTableRowContainer'

export interface UserListContainerProps {
  vm: UserManagementViewModel
}

export const UserListContainer = observer(function UserListContainer({
  vm,
}: UserListContainerProps) {
  const { shellInfo } = useFeatures(({ shell }) => ({
    shellInfo: shell.shellInfo,
  }))
  const layout = vm.columnLayout

  return (
    <UserList
      variant="users"
      isShowingInactiveUsers={vm.userSearch.userInactiveFilter.value}
      onInactiveChange={vm.userSearch.userInactiveFilter.change}
      totalUsers={vm.userSearch.totalUsers}
      showStatusTabs={vm.workspaceSelector.selectedWorkspace !== Workspace.none}
      searchText={vm.userSearch.searchText.value}
      onSearchTextChange={vm.userSearch.searchText.change}
      filterButton={
        <FilterButtonContainer
          vm={vm.userSearch.filters}
          editorTestId="job"
          testId="user-filter"
        />
      }
      appliedFilters={
        <AppliedFiltersContainer
          vm={vm.userSearch.filters}
          editorTestId="job"
          testId="user-filter"
        />
      }
      actionsMenu={
        <UserManagementTableMenuContainer
          vm={vm}
          button={<UserListActionsButton />}
        />
      }
      selection={{
        isShowing: layout.showSelection,
        onToggleShowing: vm.toggleShowSelection,
        all: match<SelectionState, boolean | 'indeterminate'>(
          vm.selectedState,
          {
            ['AllSelected']: () => true,
            ['NoneSelected']: () => false,
            ['SomeSelected']: () => 'indeterminate',
          },
        ),
        onToggleAll: vm.toggleAll,
        onToggleRow: (row, checked) => vm.toggleUser(checked, row.publicId),
      }}
      columnWidths={
        Object.fromEntries(
          userListColumns.map((column) => [column.id, layout.widthOf(column)]),
        ) as Record<UserListColumnId, number>
      }
      onResize={layout.setWidth}
      onResizeEnd={layout.save}
      showEmptyState={vm.shouldShowEmptyState}
      emptyState={
        <NoResultsEmptyState
          corporateMode={shellInfo.corporateMode}
          isSearchFilterApplied={vm.isSearchFilterApplied}
          onClearSearchFilter={vm.clearFilters}
        />
      }
      rows={vm.users.map((user) => ({
        publicId: user.publicId,
        displayName: user.displayName,
        avatar: user.avatar,
        online: user.online,
        email: user.email,
        userType: user.userDataType
          ? getUserDataTypeDisplayName(user.userDataType)
          : null,
        lastSeen: user.lastSeen,
        dateCreated: user.dateCreated,
        href: vm.getUserDetailsLinkForUser(user),
        isSelected: vm.isSelected(user.publicId),
        actions: <UserRowActionsContainer vm={vm} user={user} />,
      }))}
      isSkeleton={vm.userSearch.isFetchingInitial}
      scrollLoader={
        <SkeletonListInfiniteScrollTrigger
          scroller={vm.userSearch.scrollProvider}
          maxSkeletons={10}
        >
          <DataTableRowSkeleton />
        </SkeletonListInfiniteScrollTrigger>
      }
    />
  )
})
